import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getPhotos } from './config';
import isSSR from '@/utils/isSSR';

import './style.css';

const TimelineMax = !isSSR ? require('gsap').TimelineMax : null;

function AnimatedPhotos({ visible, isMobile, pxDuration }) {
  const [isRunning, setIsRunning] = useState(false);
  const timeline = React.useRef(null);
  const proxyTween = React.useRef(null);
  const tickIndex = React.useRef(0);
  const allowTick = React.useRef(false);
  const photoRef = React.useRef({});
  const scrollYRef = React.useRef(isSSR ? 0 : window.scrollY);
  const prevScrollY = React.useRef(isSSR ? 0 : window.scrollY);
  const prevProgress = React.useRef(0);
  const scrollDirection = React.useRef('forward');

  useEffect(() => {
    const handleScrollDirection = () => {
      scrollDirection.current =
        window.scrollY > prevScrollY.current ? 'forward' : 'backward';
      prevScrollY.current = window.scrollY;
    };

    const zoomScrollListener = () => {
      const scrollTop = window.scrollY;
      handleScrollDirection();
      // scroll %,  0-1 from component init to the pxDuration
      const scrolledPx = scrollTop - scrollYRef.current;
      // prevent animation jump when hitting top/bottom edge
      if (scrolledPx === 0) {
        return;
      }

      const scrollPercent = Math.max(
        scrolledPx > 0
          ? Math.abs(scrolledPx) / pxDuration
          : 1 - Math.abs(scrolledPx) / pxDuration,
        0
      );

      // prevent animation jump (related to beacon thresholds)
      if (
        scrollDirection.current === 'forward' &&
        prevProgress.current > scrollPercent
      ) {
        return;
      }

      if (
        scrollDirection.current === 'backward' &&
        prevProgress.current < scrollPercent
      ) {
        return;
      }

      allowTick.current = true;
      proxyTween.current.progress(scrollPercent).pause();
      prevProgress.current = scrollPercent;
    };

    const handleTick = () => {
      if (!allowTick.current) {
        return;
      }

      // NOTE: on first frame when the boxWrapper was visible, the boxes where
      //       fully visible (opacity: 1), so we wait the second tick to show the boxWrapper
      // skip first tick
      if (!tickIndex.current) {
        tickIndex.current += 1;
        return;
      }
      // on second tick set isRunning to true to show the block
      if (tickIndex.current === 1) {
        tickIndex.current += 1;
        setIsRunning(true);
      }

      let progress = timeline.current.progress();
      // ease can be anything from 0.5 to 0.01
      progress += (proxyTween.current.progress() - progress) * 0.1;
      timeline.current.progress(progress);
    };

    if (visible) {
      tickIndex.current = 0;
      scrollYRef.current = window.scrollY;
      window.addEventListener('scroll', zoomScrollListener);
      TweenLite.ticker.addEventListener('tick', handleTick);
    }
    return () => {
      if (visible) {
        window.removeEventListener('scroll', zoomScrollListener);
        TweenLite.ticker.removeEventListener('tick', handleTick);
      }
    };
  }, [visible]);

  useEffect(() => {
    TweenLite.defaultEase = Linear.easeNone;
    timeline.current = new TimelineMax({ paused: true }).progress(0).pause();
    proxyTween.current = TweenLite.to({}, 1, { paused: true });
    proxyTween.current.progress(0).pause();

    Object.values(photoRef.current).map((node, idx) => {
      const initialYPosition =
        node.style.top === '100%' ? '0' : node.style.top === '0%' ? '-150%' : 0;

      timeline.current
        .set(
          node,
          {
            alpha: 0,
            zIndex: getPhotos({ mobile: isMobile }).length - idx,
          },
          0
        )
        .add(`photo${idx}`, idx / 2)
        .fromTo(
          node,
          1,
          {
            y: initialYPosition,
            z: `-${(isMobile ? 400 : 1000) - idx * 1}px`,
          },
          {
            y: 0,
            z: `${100 - idx * 1}px`,
          },
          `photo${idx}`
        )
        .fromTo(
          node,
          0.15,
          {
            alpha: 0,
          },
          {
            alpha: 1,
          },
          `photo${idx}`
        )
        .fromTo(
          node,
          0.15,
          {
            alpha: 1,
          },
          {
            alpha: 0,
          },
          `photo${idx}+=0.85`
        );
    });
  }, []);

  const photoArray = React.useMemo(
    () =>
      isMobile
        ? getPhotos({ mobile: true }).map(photo => ({
            ...photo,
            y: Math.round(Math.random()),
          }))
        : getPhotos({ mobile: false }),
    [isMobile]
  );

  return (
    <div
      className={classnames('boxWrapper', {
        'boxWrapper--visible': visible && isRunning,
      })}
    >
      {photoArray.map(photo => {
        return (
          <div
            key={photo.id}
            className="box"
            ref={ref => {
              photoRef.current[photo.id] = ref;
            }}
            style={{
              ...(photo.x > 0.5
                ? { left: `${50 + photo.x * 10}%` }
                : { right: `${50 + photo.x * 10}%` }),
              top: `${photo.y * 100}%`,
            }}
          >
            <img src={photo.url} />
          </div>
        );
      })}
    </div>
  );
}

AnimatedPhotos.propTypes = {
  visible: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  pxDuration: PropTypes.number.isRequired,
};

export default AnimatedPhotos;
